import React, { useState } from 'react';
import { kontakService } from '../../services/kontakService';
import {
    FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock,
    FaCheckCircle, FaExclamationTriangle, FaSpinner, FaPaperPlane
} from 'react-icons/fa';

const KontakPage = () => {
    const [formData, setFormData] = useState({
        nama: '',
        email: '',
        subjek: '',
        pesan: '',
    });

    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [notification, setNotification] = useState(null);

    // ✅ Sanitize input di frontend - cegah script tag
    const sanitizeInput = (value) => {
        if (typeof value !== 'string') return '';
        // Escape HTML special chars
        return value
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        // ✅ Sanitize saat input
        const sanitizedValue = sanitizeInput(value);
        setFormData(prev => ({ ...prev, [name]: sanitizedValue }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.nama.trim()) newErrors.nama = 'Nama wajib diisi';
        if (!formData.email.trim()) newErrors.email = 'Email wajib diisi';
        else if (!/^\S+@\S+\.\S+$/.test(formData.email)) newErrors.email = 'Format email tidak valid';
        if (!formData.pesan.trim()) newErrors.pesan = 'Pesan wajib diisi';
        else if (formData.pesan.length < 10) newErrors.pesan = 'Pesan minimal 10 karakter';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setSubmitting(true);
        setNotification(null);

        const result = await kontakService.sendKontak(formData);

        if (result.success) {
            setNotification({
                type: 'success',
                message: result.message || 'Pesan Anda telah terkirim. Kami akan segera merespon.'
            });
            // Reset form
            setFormData({ nama: '', email: '', subjek: '', pesan: '' });
            setErrors({});
        } else {
            setNotification({
                type: 'error',
                message: result.message || 'Gagal mengirim pesan. Silakan coba lagi.'
            });
            if (result.errors) setErrors(result.errors);
        }
        setSubmitting(false);
    };

    return (
        <div className="container mx-auto px-4 py-12">
            <h1 className="text-3xl font-bold text-[#0F4C3A] mb-8">Kontak Kami</h1>

            {notification && (
                <div className={`mb-6 p-4 rounded-lg flex items-center ${notification.type === 'success'
                        ? 'bg-green-50 border border-green-200 text-green-700'
                        : 'bg-red-50 border border-red-200 text-red-700'
                    }`}>
                    {notification.type === 'success'
                        ? <FaCheckCircle className="mr-2 flex-shrink-0" />
                        : <FaExclamationTriangle className="mr-2 flex-shrink-0" />}
                    {notification.message}
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Info Kontak */}
                <div className="card-glass">
                    <h2 className="text-xl font-bold text-[#2E7D32] mb-6">Informasi Kontak</h2>

                    <div className="space-y-4">
                        <div className="flex items-start space-x-3">
                            <FaMapMarkerAlt className="text-[#0F4C3A] text-xl mt-1 flex-shrink-0" />
                            <div>
                                <h3 className="font-semibold">Alamat</h3>
                                <p className="text-gray-600">Jl. Jend. Sudirman No. 10, Magelang, Jawa Tengah 56111</p>
                            </div>
                        </div>

                        <div className="flex items-center space-x-3">
                            <FaPhone className="text-[#0F4C3A] text-xl flex-shrink-0" />
                            <div>
                                <h3 className="font-semibold">Telepon</h3>
                                <p className="text-gray-600">(0293) 123456</p>
                            </div>
                        </div>

                        <div className="flex items-center space-x-3">
                            <FaEnvelope className="text-[#0F4C3A] text-xl flex-shrink-0" />
                            <div>
                                <h3 className="font-semibold">Email</h3>
                                <p className="text-gray-600">bpkad@magelangkota.go.id</p>
                            </div>
                        </div>

                        <div className="flex items-start space-x-3">
                            <FaClock className="text-[#0F4C3A] text-xl mt-1 flex-shrink-0" />
                            <div>
                                <h3 className="font-semibold">Jam Operasional</h3>
                                <p className="text-gray-600">Senin - Kamis: 07.30 - 16.00 WIB</p>
                                <p className="text-gray-600">Jumat: 07.30 - 16.30 WIB</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Form */}
                <div className="card-glass">
                    <h2 className="text-xl font-bold text-[#2E7D32] mb-6">Kirim Pesan</h2>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Nama <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="nama"
                                value={formData.nama}
                                onChange={handleInputChange}
                                maxLength="100"
                                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent ${errors.nama ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                    }`}
                                placeholder="Masukkan nama lengkap"
                            />
                            {errors.nama && <p className="text-red-500 text-xs mt-1">{errors.nama}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Email <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                maxLength="100"
                                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent ${errors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                    }`}
                                placeholder="contoh@email.com"
                            />
                            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Subjek
                            </label>
                            <input
                                type="text"
                                name="subjek"
                                value={formData.subjek}
                                onChange={handleInputChange}
                                maxLength="200"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                                placeholder="Subjek pesan (opsional)"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Pesan <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                name="pesan"
                                value={formData.pesan}
                                onChange={handleInputChange}
                                rows="5"
                                maxLength="2000"
                                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent ${errors.pesan ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                    }`}
                                placeholder="Tulis pesan Anda..."
                            />
                            <div className="flex justify-between items-center mt-1">
                                {errors.pesan ? (
                                    <p className="text-red-500 text-xs">{errors.pesan}</p>
                                ) : <span></span>}
                                <span className="text-xs text-gray-400">
                                    {formData.pesan.length}/2000
                                </span>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={submitting}
                            className="btn-primary w-full flex items-center justify-center disabled:opacity-50"
                        >
                            {submitting ? (
                                <>
                                    <FaSpinner className="animate-spin mr-2" />
                                    Mengirim...
                                </>
                            ) : (
                                <>
                                    <FaPaperPlane className="mr-2" />
                                    Kirim Pesan
                                </>
                            )}
                        </button>

                        <p className="text-xs text-gray-500 text-center">
                            Pesan Anda akan tersimpan dengan aman. Kami akan merespon secepat mungkin.
                        </p>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default KontakPage;